import React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import SearchBox from 'components/SearchBox'
import ProfileDropdown from 'app/RuangGTKHomepageV3/components/ProfileDropdown'
import { assetUrl } from 'utils/url'
import { useSETrackEvent } from 'app/Homepage/hooks/useSETrackEvent'
import { setInformationPageNavigationOrigin } from 'app/InfoTerkiniPage/utils/navigationOrigin'
import { useMediaQueryHelper } from 'app/RuangGTKHomepageV3/hooks/useMediaQueryHelper'
import {
  useContentSearchOnboarding,
  useContentSearchTracker,
} from 'stores/content-search'
import containerStyles from 'components/ResponsiveLayout/container.module.css'

type NavLink = {
  label: string
  href: string
  external?: boolean
}

const DEFAULT_NAVIGATION_LINKS: NavLink[] = [
  { label: 'Beranda', href: '/' },
  { label: 'Pemberitahuan', href: '/info-terkini' },
]

interface HeaderProps {
  showSearch?: boolean
  breadcrumb?: React.ReactNode
  navLinks?: NavLink[]
}

const Header = ({
  showSearch = true,
  breadcrumb,
  navLinks = DEFAULT_NAVIGATION_LINKS,
}: HeaderProps) => {
  const router = useRouter()
  const { isDesktop } = useMediaQueryHelper()
  const { SETrackClickSearchBar } = useSETrackEvent()
  const contentSearchOnboardingStore = useContentSearchOnboarding()
  const contentSearchTrackerStore = useContentSearchTracker()

  const clickSearchHandler = React.useCallback(async () => {
    contentSearchTrackerStore.setSource('HOMEPAGE_SEARCH_BAR')
    SETrackClickSearchBar()
    await router.push('/pencarian')

    if (!contentSearchOnboardingStore.isFinished) {
      contentSearchOnboardingStore.setFinished()
    }
  }, [
    SETrackClickSearchBar,
    contentSearchOnboardingStore,
    contentSearchTrackerStore,
    router,
  ])

  const clickNavLinkHandler = React.useCallback(
    (href: string) => {
      if (href === '/info-terkini') {
        setInformationPageNavigationOrigin(router.route ?? router.pathname)
      }
    },
    [router.pathname, router.route]
  )

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#2A7EFB]">
        <div
          className={clsx(
            containerStyles['rgtk-page-container'],
            'mx-auto flex w-full flex-col gap-5 md:gap-8 md:!pt-8'
          )}
        >
          {/* Logo and Navigation Section */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <Link href="/">
              <a className="flex items-center gap-4">
                <img
                  src={assetUrl('/images/ruang-gtk/logo-sm-inverse.svg')}
                  alt="Logo"
                  className="h-8 w-auto md:h-10"
                />
                <Text
                  variant={isDesktop ? 'heading-md' : 'heading-sm'}
                  color="inverse"
                >
                  Ruang Guru dan Tenaga Kependidikan (GTK)
                </Text>
              </a>
            </Link>

            <nav className="hidden items-center gap-6 text-sm font-medium text-white md:flex xl:gap-8">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer hover:opacity-80"
                  >
                    <Text variant="heading-md" color="inverse">
                      {link.label}
                    </Text>
                  </a>
                ) : (
                  <Link key={link.href} href={link.href}>
                    <span
                      className="cursor-pointer hover:opacity-80"
                      onClick={() => clickNavLinkHandler(link.href)}
                    >
                      <Text variant="heading-md" color="inverse">
                        {link.label}
                      </Text>
                    </span>
                  </Link>
                )
              )}
              <ProfileDropdown />
            </nav>
          </div>

          {/* Breadcrumb Section (alternative to search) */}
          {!showSearch && breadcrumb && (
            <div className="w-full pb-2">{breadcrumb}</div>
          )}
        </div>
      </header>

      {/* SearchBox Section */}
      {showSearch && (
        <div className="relative z-40 w-full bg-[#2A7EFB]">
          <div
            className={clsx(
              containerStyles['rgtk-page-container'],
              'mx-auto w-full !pt-2 md:!pt-4 lg:!pt-2'
            )}
          >
            <SearchBox
              placeholder="Cari Layanan.."
              readOnly
              onClick={clickSearchHandler}
              onClickButton={clickSearchHandler}
              className="bg-white"
            />
          </div>
        </div>
      )}
    </>
  )
}

export default Header
